import { Configuration, SuppliersApi } from '@/api';
import useGetSupplierQuery from '@/api/queries/useGetSupplierQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { isBillingEnabledFor } from '@/config';
import { SupplierAddressType, SupplierCreate, SupplierUpdate } from '@/models/Supplier';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import SupplierBillingPanel from '@/modules/admin/components/SupplierBillingPanel';
import SupplierForm from '@/modules/admin/components/SupplierForm';
import {
  SupplierEditPath,
  SupplierEditSectionPath,
  SupplierListPath,
  SupplierToRetailerListPath,
  SupplierToStoreListPath,
} from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

type SupplierEditTab = 'details' | 'business-address' | 'billing';

const allTabs: Array<{
  id: SupplierEditTab;
  label: string;
  description: string;
}> = [
  {
    id: 'details',
    label: 'Details',
    description: 'Update the supplier profile, notification email, and activation status.',
  },
  {
    id: 'business-address',
    label: 'Business Address',
    description: 'Primary legal and operational address for the supplier.',
  },
  {
    id: 'billing',
    label: 'Billing',
    description: 'Manage subscription details and billing information for this supplier.',
  },
];

const SupplierEdit = () => {
  const navigate = useNavigate();
  const { supplierId, section } = useParams();
  const tabs = isBillingEnabledFor('supplier')
    ? allTabs
    : allTabs.filter((tab) => tab.id !== 'billing');
  const activeTab: SupplierEditTab =
    section === 'business-address' || (section === 'billing' && isBillingEnabledFor('supplier'))
      ? section
      : 'details';
  const selectedTab = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const { isLoading, supplier, invalidateQuery } = useGetSupplierQuery(supplierId);

  function isSupplierUpdate(supplier: SupplierCreate | SupplierUpdate): supplier is SupplierUpdate {
    return 'id' in supplier;
  }

  const handleOnSubmit = async (supplier: SupplierCreate | SupplierUpdate): Promise<void> => {
    if (!isSupplierUpdate(supplier)) {
      return;
    }

    try {
      await suppliersApi.updateSupplier(supplier.id, supplier);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Supplier "${supplier.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery(true);

      navigate(SupplierListPath);
    } catch (error) {
      console.error('Failed to update supplier:', error);
    }
  };

  const handleOnDelete = async () => {
    if (!supplier?.id) {
      return;
    }

    try {
      await suppliersApi.deleteSupplier(supplier.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Supplier "${supplier.name}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      navigate(SupplierListPath);
    } catch (error) {
      console.error('Failed to delete supplier:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!supplier) {
    return null;
  }

  const handleTabChange = (tabId: SupplierEditTab) => {
    if (!supplierId) {
      return;
    }

    if (tabId === 'details') {
      navigate(SupplierEditPath.withId(supplierId), { replace: true });
      return;
    }

    navigate(SupplierEditSectionPath.withParams(supplierId, tabId), { replace: true });
  };

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Edit Supplier">
        <Button to={SupplierToRetailerListPath.withSupplierId(supplier.id)} variant="secondary">
          Retailers
        </Button>
        <Button to={SupplierToStoreListPath.withSupplierId(supplier.id)} variant="secondary">
          Stores
        </Button>
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete Supplier"
          message={`Are you sure you want to delete supplier "${supplier.name}"? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Supplier
            </Button>
          )}
        </Confirm>
      </PageHeader>

      <div className="w-full bg-white mx-auto rounded-lg shadow p-6 lg:p-12">
        <div className="mb-8 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="mt-3 px-1 text-sm text-gray-500">{selectedTab.description}</div>
        </div>

        {activeTab === 'billing' ? (
          <SupplierBillingPanel supplier={supplier} />
        ) : activeTab === 'details' ? (
          <SupplierForm onSubmit={handleOnSubmit} supplier={supplier} mode="details" />
        ) : (
          <SupplierForm
            onSubmit={handleOnSubmit}
            supplier={supplier}
            mode="address"
            activeAddressType={SupplierAddressType.BUSINESS}
          />
        )}
      </div>
    </>
  );
};

export default SupplierEdit;
